/**
 * CreateLeadModal — Institutional Lead Wizard 2.0 (P2 CRM)
 *
 * 4 steps:
 *   1) Identity         — company name (autocomplete), contact, email, phone
 *   2) Classification   — role, country, industry, source, tags, score
 *   3) Compliance       — UAE: Trade License + TRN + EID  |  Foreign: Cert + Tax ID + Passport
 *   4) Commercial       — currency, est. value, assigned, notes  +  triggered-workflows preview
 *
 * On submit, backend auto-triggers:
 *   • Master Counterparty Registry (P2)
 *   • CRM Contact (P2)
 *   • PEP screening (P5)
 *   • EDD case if high-risk country (P5)
 *   • Procurement supplier candidate if role=supplier (P6)
 *   • P7 Event Bus event "lead.created"
 *   • KYC pending status (P8)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  X, Search, ShieldCheck, FileBadge, Banknote, ChevronLeft,
  ChevronRight, Loader2, Sparkles, AlertTriangle, Building2,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'supplier',  label: 'Supplier' },
  { value: 'client',    label: 'Client' },
  { value: 'investor',  label: 'Investor' },
  { value: 'partner',   label: 'Partner' },
  { value: 'other',     label: 'Other' },
];

const SOURCE_OPTIONS = [
  'website', 'referral', 'cold_call', 'event', 'linkedin',
  'campaign', 'partner', 'other',
];

const HIGH_RISK = new Set([
  'Afghanistan', 'Myanmar', 'North Korea', 'Iran', 'Syria', 'Yemen',
  'Somalia', 'Libya', 'Venezuela', 'Russia', 'Belarus', 'Sudan',
  'South Sudan', 'Democratic Republic of the Congo', 'Zimbabwe', 'Haiti', 'Cuba',
]);

const isUAE = (c) => {
  if (!c) return false;
  const v = c.trim().toLowerCase();
  return ['ae', 'uae', 'u.a.e.', 'united arab emirates'].includes(v);
};

const empty = {
  company_name: '', contact_person: '', email: '', phone: '',
  role: 'client', country: '', industry: '', language: '',
  source: '', tags: '', lead_score: 0, status: 'new',
  trade_license: '', trn: '', eid_or_passport: '',
  cert_of_incorporation: '', tax_id: '',
  currency: 'AED', estimated_value: '', assigned_to: '', notes: '',
};

const CreateLeadModal = ({ isOpen, onClose, onSuccess }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const lookupTimer = useRef(null);

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!isOpen) {
      setStep(1); setData(empty); setSuggestions([]); setShowSug(false);
    }
  }, [isOpen]);

  // Autocomplete on company name
  useEffect(() => {
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    if (!data.company_name || data.company_name.length < 2) {
      setSuggestions([]); setShowSug(false); return;
    }
    lookupTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/crm/leads/lookup?q=${encodeURIComponent(data.company_name)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (r.ok) {
          const j = await r.json();
          setSuggestions(j); setShowSug(true);
        }
      } catch (_) { /* silent */ }
    }, 250);
    return () => lookupTimer.current && clearTimeout(lookupTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.company_name]);

  const set = (field) => (e) => {
    const v = e?.target ? e.target.value : e;
    setData((p) => ({ ...p, [field]: v }));
  };

  const applySuggestion = (s) => {
    setData((p) => ({
      ...p,
      company_name:    s.company_name || p.company_name,
      contact_person:  s.contact_person || p.contact_person,
      email:           s.email || p.email,
      phone:           s.phone || p.phone,
      country:         s.country || p.country,
      role:            s.role || p.role,
      industry:        s.industry || p.industry,
      currency:        s.currency || p.currency,
      trade_license:   s.trade_license || p.trade_license,
      trn:             s.trn || p.trn,
    }));
    setShowSug(false);
    toast({ title: 'Auto-filled', description: `Imported from ${s.source}` });
  };

  const validateStep = () => {
    if (step === 1) {
      return data.company_name.trim() && data.contact_person.trim()
        && data.email.trim() && /\S+@\S+\.\S+/.test(data.email);
    }
    if (step === 2) return !!data.role && !!data.country;
    if (step === 3) {
      // Soft validation — encourage fill but don't block
      return true;
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) {
      toast({ title: 'Missing required fields',
        description: 'Please complete the highlighted fields.',
        variant: 'destructive' });
      return;
    }
    setStep((s) => Math.min(4, s + 1));
  };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        tags: data.tags ? data.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        lead_score: parseInt(data.lead_score) || 0,
        estimated_value: data.estimated_value ? parseFloat(data.estimated_value) : null,
      };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' || payload[k] === null) delete payload[k];
      });
      const r = await fetch(`${API_URL}/api/crm/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create lead');
      }
      const created = await r.json();
      toast({
        title: 'Lead created',
        description: `${created.company_name} — workflows triggered.`,
      });
      onSuccess && onSuccess(created);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const willTrigger = useMemo(() => {
    const t = ['Master Counterparty', 'CRM Contact', 'PEP screening', 'P7 event'];
    if ((data.role || '').toLowerCase() === 'supplier') t.push('Procurement supplier');
    if (HIGH_RISK.has((data.country || '').trim())) t.push('EDD case (high-risk)');
    t.push('KYC pending');
    return t;
  }, [data.role, data.country]);

  if (!isOpen) return null;
  const uae = isUAE(data.country);
  const hr = HIGH_RISK.has((data.country || '').trim());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      data-testid="create-lead-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="create-lead-modal">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-[#0F172A] to-[#1E293B] text-white p-5 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A86A]">
              Lead Wizard · Step {step} of 4
            </div>
            <h3 className="text-xl font-serif">Add New Lead</h3>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white"
            data-testid="create-lead-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="px-6 pt-5 flex items-center gap-1.5">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className={`h-1.5 flex-1 rounded-full transition-all ${
              n <= step ? 'bg-[#C9A86A]' : 'bg-slate-200'
            }`} />
          ))}
        </div>

        <div className="p-6">
          {/* Step 1 — Identity */}
          {step === 1 && (
            <div className="space-y-4" data-testid="lead-step-identity">
              <SectionTitle icon={Building2} title="Identity" subtitle="Who is the lead?" />
              <div className="relative">
                <label className="text-xs uppercase tracking-wider text-slate-600">
                  Company Name <span className="text-rose-500">*</span>
                </label>
                <div className="relative mt-1">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-3 text-slate-400" />
                  <Input
                    data-testid="lead-input-company-name"
                    className="pl-9"
                    value={data.company_name}
                    onChange={set('company_name')}
                    placeholder="Start typing… auto-fill from existing platform data"
                    onFocus={() => suggestions.length && setShowSug(true)}
                    onBlur={() => setTimeout(() => setShowSug(false), 200)}
                  />
                  {showSug && suggestions.length > 0 && (
                    <ul
                      data-testid="lead-autocomplete-list"
                      className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                      {suggestions.map((s, i) => (
                        <li
                          key={i}
                          data-testid={`lead-autocomplete-item-${i}`}
                          onMouseDown={() => applySuggestion(s)}
                          className="px-3 py-2.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{s.label}</span>
                            <Badge className="bg-slate-100 text-slate-700 text-[9px] uppercase">
                              {s.source}
                            </Badge>
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {[s.country, s.role, s.email].filter(Boolean).join(' · ') || '—'}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <Field label="Contact Person *" testid="lead-input-contact-person"
                value={data.contact_person} onChange={set('contact_person')} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email *" type="email" testid="lead-input-email"
                  value={data.email} onChange={set('email')} />
                <Field label="Phone" testid="lead-input-phone"
                  value={data.phone} onChange={set('phone')} />
              </div>
            </div>
          )}

          {/* Step 2 — Classification */}
          {step === 2 && (
            <div className="space-y-4" data-testid="lead-step-classification">
              <SectionTitle icon={Sparkles} title="Classification"
                subtitle="Role + jurisdiction drive the workflow chain" />
              <div className="grid grid-cols-2 gap-3">
                <Select label="Role *" testid="lead-select-role"
                  value={data.role} onChange={set('role')} options={ROLE_OPTIONS} />
                <Field label="Country *" testid="lead-input-country"
                  value={data.country} onChange={set('country')}
                  placeholder="e.g. United Arab Emirates" />
              </div>
              {hr && (
                <div data-testid="lead-high-risk-banner"
                  className="text-xs px-3 py-2 rounded-lg bg-orange-50 border border-orange-200 text-orange-800 flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
                  <span>This jurisdiction is on the FATF high-risk list. An EDD
                  case will be auto-created on submission.</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Industry" testid="lead-input-industry"
                  value={data.industry} onChange={set('industry')}
                  placeholder="e.g. trading, real estate" />
                <Field label="Language" testid="lead-input-language"
                  value={data.language} onChange={set('language')}
                  placeholder="e.g. en, pt, ar" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Select label="Source" testid="lead-select-source"
                  value={data.source} onChange={set('source')}
                  options={[{ value: '', label: '—' }, ...SOURCE_OPTIONS.map((s) => ({ value: s, label: s }))]} />
                <Field label="Lead Score (0-100)" type="number"
                  testid="lead-input-score"
                  value={data.lead_score} onChange={set('lead_score')} />
              </div>
              <Field label="Tags (comma-separated)" testid="lead-input-tags"
                value={data.tags} onChange={set('tags')}
                placeholder="e.g. priority, q4-2026" />
            </div>
          )}

          {/* Step 3 — Compliance */}
          {step === 3 && (
            <div className="space-y-4" data-testid="lead-step-compliance">
              <SectionTitle icon={ShieldCheck} title="Compliance"
                subtitle={uae ? 'UAE-compliant fields — required for KYC pack' : 'International compliance fields'} />
              {uae ? (
                <>
                  <Field label="Trade License (DED)" testid="lead-input-trade-license"
                    value={data.trade_license} onChange={set('trade_license')} />
                  <Field label="TRN (FTA)" testid="lead-input-trn"
                    value={data.trn} onChange={set('trn')}
                    placeholder="15-digit FTA tax registration number" />
                  <Field label="Emirates ID / Passport" testid="lead-input-eid"
                    value={data.eid_or_passport} onChange={set('eid_or_passport')} />
                </>
              ) : (
                <>
                  <Field label="Certificate of Incorporation" testid="lead-input-cert-incorp"
                    value={data.cert_of_incorporation} onChange={set('cert_of_incorporation')} />
                  <Field label="Tax ID" testid="lead-input-tax-id"
                    value={data.tax_id} onChange={set('tax_id')} />
                  <Field label="Passport" testid="lead-input-passport"
                    value={data.eid_or_passport} onChange={set('eid_or_passport')} />
                </>
              )}
              <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
                These values are pushed automatically into the Master Counterparty
                Registry and reused by P5 (Risk), P6 (Procurement), and P8 (KYC).
              </div>
            </div>
          )}

          {/* Step 4 — Commercial + workflows preview */}
          {step === 4 && (
            <div className="space-y-4" data-testid="lead-step-commercial">
              <SectionTitle icon={Banknote} title="Commercial"
                subtitle="Value & assignment, then review the workflow chain" />
              <div className="grid grid-cols-2 gap-3">
                <Select label="Currency" testid="lead-select-currency"
                  value={data.currency} onChange={set('currency')}
                  options={['AED', 'USD', 'EUR', 'GBP', 'BRL'].map((c) => ({ value: c, label: c }))} />
                <Field label="Estimated Value" type="number"
                  testid="lead-input-estimated-value"
                  value={data.estimated_value} onChange={set('estimated_value')}
                  placeholder="e.g. 250000" />
              </div>
              <Field label="Assigned to (user id)" testid="lead-input-assigned"
                value={data.assigned_to} onChange={set('assigned_to')} />
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-600">
                  Notes
                </label>
                <Textarea
                  data-testid="lead-input-notes"
                  rows={3} className="mt-1"
                  value={data.notes} onChange={set('notes')} />
              </div>

              {/* Workflows preview */}
              <div className="mt-4 p-4 rounded-xl bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white">
                <div className="flex items-center gap-2 mb-2">
                  <FileBadge className="w-4 h-4 text-[#C9A86A]" />
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#C9A86A]">
                    Workflows that will be triggered
                  </span>
                </div>
                <ul className="text-sm space-y-1" data-testid="lead-workflow-preview">
                  {willTrigger.map((w) => (
                    <li key={w} className="flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#C9A86A]" />
                      {w}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex items-center justify-between">
          <Button variant="ghost" onClick={prev} disabled={step === 1 || loading}
            data-testid="lead-prev-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {step < 4 ? (
            <Button onClick={next} className="bg-[#0F172A] hover:bg-[#1E293B]"
              data-testid="lead-next-btn">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={loading}
              className="bg-[#C9A86A] hover:bg-[#B89860] text-black"
              data-testid="lead-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Lead & Trigger Workflows
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const SectionTitle = ({ icon: Icon, title, subtitle }) => (
  <div className="border-b border-slate-100 pb-3 mb-2">
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-[#C9A86A]" />
      <h4 className="font-medium text-[#0F172A]">{title}</h4>
    </div>
    {subtitle && <div className="text-xs text-slate-500 mt-0.5 ml-6">{subtitle}</div>}
  </div>
);

const Field = ({ label, value, onChange, type = 'text', testid, placeholder }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-slate-600">{label}</label>
    <Input
      type={type}
      data-testid={testid}
      className="mt-1"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
    />
  </div>
);

const Select = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-slate-600">{label}</label>
    <select
      data-testid={testid}
      className="mt-1 w-full h-9 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A86A]/30"
      value={value || ''}
      onChange={onChange}>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

export default CreateLeadModal;
